import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import { mergeWhere } from '@/lib/table-query';
import { useAuthStore } from '@/state/store/auth';
import {
  clockIn,
  clockOut,
  endBreak,
  getAttendanceCorrections,
  getAttendanceDays,
  getMyFineSummary,
  getMyHrTerms,
  getTodayAttendance,
  startBreak,
  submitAttendanceCorrection,
} from '../services/attendance.service';
import {
  AttendanceRuleError,
  attendanceDateClause,
  attendanceDateRangeClause,
  attendanceDayOf,
  getOrgDateString,
} from '../services/attendance-rules';
import {
  AttendanceCorrection,
  BreakTimeDetail,
  BreakType,
  GetAttendanceCorrectionsParams,
} from '../types/attendance.types';

/** The logged-in user's IAM id — Attendance/AttendanceCorrection records key on this via `UserId`. */
export const useCurrentUserId = () => {
  const user = useAuthStore((state) => state.user);
  return user?.itemId ?? '';
};

/** Today's own Attendance record; "today" is the day on the organization's clock. */
export const useGetTodayAttendance = () => {
  const userId = useCurrentUserId();
  return useGlobalQuery({
    queryKey: ['hr-attendance-today', userId],
    queryFn: () => getTodayAttendance(userId),
    enabled: Boolean(userId),
  });
};

/** Every day from `from` to `to` ("YYYY-MM-DD") of `userId`'s attendance, with what each day without a
 * record is (leave, holiday, weekend, absent). Shared with Manage Attendance's history. */
export const useGetAttendanceDays = (userId: string, from: string, to: string) =>
  useGlobalQuery({
    queryKey: ['hr-attendance-days', userId, from, to],
    queryFn: () => getAttendanceDays({ userId, from, to }),
    enabled: Boolean(userId && from && to),
    staleTime: 60 * 1000,
  });

/** Current-HR-year fine breakdown for the clock widget. */
export const useGetMyFineSummary = () => {
  const userId = useCurrentUserId();
  return useGlobalQuery({
    queryKey: ['hr-attendance-fine-summary', userId],
    queryFn: () => getMyFineSummary(userId),
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
  });
};

/** The signed-in employee's HR terms with their work days and hours. */
export const useGetMyHrTerms = () => {
  const userId = useCurrentUserId();
  return useGlobalQuery({
    queryKey: ['hr-attendance-hr-terms', userId],
    queryFn: () => getMyHrTerms(userId),
    enabled: Boolean(userId),
  });
};

export const useGetMyAttendanceCorrections = (params: GetAttendanceCorrectionsParams) => {
  const userId = useCurrentUserId();
  return useGlobalQuery({
    queryKey: ['hr-attendance-corrections', userId, params],
    // Column filters are merged with, never replace, the own-records constraint.
    queryFn: () =>
      getAttendanceCorrections({
        ...params,
        filter: mergeWhere({ UserId: userId }, params.filter),
      }),
    enabled: Boolean(userId),
    staleTime: 60 * 1000,
  });
};

/** The status of each day's correction from `from` to `to` ("YYYY-MM-DD") of `userId`, by day: a
 * pending one when there is one (one per day, the request workflow refuses a second), else the
 * latest. */
export const useGetCorrectionStatusByDay = (
  userId: string,
  from: string,
  to: string,
  enabled = true
) =>
  useGlobalQuery({
    // Under 'hr-attendance-corrections', so a submitted correction refreshes it too.
    queryKey: ['hr-attendance-corrections', userId, 'by-day', from, to],
    queryFn: async () => {
      const result = await getAttendanceCorrections({
        pageNo: 1,
        pageSize: 500,
        filter: { UserId: userId, CorrectionDate: attendanceDateRangeClause(from, to) },
      });
      // Latest first (DEFAULT_ATTENDANCE_CORRECTIONS_ORDER), so the first one of a day is its latest.
      const byDay: Record<string, AttendanceCorrection['Status']> = {};
      for (const correction of result.AttendanceCorrections.items) {
        const day = attendanceDayOf(correction.CorrectionDate);
        if (!byDay[day] || correction.Status === 'Pending') byDay[day] = correction.Status;
      }
      return byDay;
    },
    enabled: Boolean(enabled && userId && from && to),
    staleTime: 60 * 1000,
  });

/** Whether a correction of today (organization day) waits for review: the clock-in workflow refuses
 * then (its approval replaces the day), so the widget offers no clock-in. */
export const useHasPendingCorrectionToday = () => {
  const userId = useCurrentUserId();
  const today = getOrgDateString(new Date());
  const { data, isLoading } = useGlobalQuery({
    // Under 'hr-attendance-corrections', so every invalidation of the corrections refreshes it too.
    queryKey: ['hr-attendance-corrections', userId, 'pending-today', today],
    queryFn: () =>
      getAttendanceCorrections({
        pageNo: 1,
        pageSize: 1,
        filter: { UserId: userId, Status: 'Pending', CorrectionDate: attendanceDateClause(today) },
      }),
    enabled: Boolean(userId),
    // A reviewer may decide it any moment: re-read soon and when the tab comes back.
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
  });
  return {
    hasPending: (data?.AttendanceCorrections.totalCount ?? 0) > 0,
    // Until it answers, the clock can't tell whether Clock In is allowed.
    isLoading: isLoading,
  };
};

const invalidateAttendanceQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['hr-attendance-today'] });
  queryClient.invalidateQueries({ queryKey: ['hr-attendances'] });
  queryClient.invalidateQueries({ queryKey: ['hr-attendance-days'] });
};

/**
 * The services throw `AttendanceRuleError`s whose message is a translation key (they have no `t`);
 * the global mutation error toast shows `error.message` as-is, so it is translated here first.
 */
/** Rethrows an `AttendanceRuleError` as its translated message, for the mutation's error toast. */
export const useTranslateRuleErrors = () => {
  const { t } = useTranslation();
  return async <T>(run: () => Promise<T>): Promise<T> => {
    try {
      return await run();
    } catch (error) {
      if (error instanceof AttendanceRuleError)
        throw new Error(
          t(error.message, { ...error.params, defaultValue: error.defaultMessage ?? error.message })
        );
      throw error;
    }
  };
};

/** The workflow's own success key (e.g. CLOCKED_IN_AGAIN), with its English text as the fallback. */
const successTitle = (
  t: (key: string, options?: Record<string, unknown>) => string,
  result: { Message?: string; Data?: unknown },
  fallbackKey: string
) => {
  const key = (result.Data as { MessageKey?: string } | null)?.MessageKey;
  return t(key || fallbackKey, { defaultValue: result.Message || t(fallbackKey) });
};

export const useClockIn = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const translateRuleErrors = useTranslateRuleErrors();

  return useGlobalMutation({
    mutationFn: () => translateRuleErrors(() => clockIn()),
    onSuccess: (result) => {
      invalidateAttendanceQueries(queryClient);
      toast({ title: successTitle(t, result, 'CLOCKED_IN') });
    },
    // A refusal often means the stored day moved on (another tab, the scheduler) or a correction of
    // today is pending: show both as stored.
    onError: () => {
      invalidateAttendanceQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ['hr-attendance-corrections'] });
    },
  });
};

export const useClockOut = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const translateRuleErrors = useTranslateRuleErrors();

  return useGlobalMutation({
    mutationFn: (params: { enteredBreaks?: BreakTimeDetail[]; captchaToken?: string }) =>
      translateRuleErrors(() => clockOut(params)),
    onSuccess: (result) => {
      invalidateAttendanceQueries(queryClient);
      toast({ title: successTitle(t, result, 'CLOCKED_OUT') });
    },
    onError: () => invalidateAttendanceQueries(queryClient),
  });
};

export const useStartBreak = () => {
  const queryClient = useQueryClient();
  const translateRuleErrors = useTranslateRuleErrors();

  return useGlobalMutation({
    mutationFn: (params: { type: BreakType }) => translateRuleErrors(() => startBreak(params)),
    onSuccess: () => invalidateAttendanceQueries(queryClient),
    onError: () => invalidateAttendanceQueries(queryClient),
  });
};

export const useEndBreak = () => {
  const queryClient = useQueryClient();
  const translateRuleErrors = useTranslateRuleErrors();

  return useGlobalMutation({
    mutationFn: () => translateRuleErrors(() => endBreak()),
    onSuccess: () => invalidateAttendanceQueries(queryClient),
    onError: () => invalidateAttendanceQueries(queryClient),
  });
};

export const useSubmitAttendanceCorrection = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const translateRuleErrors = useTranslateRuleErrors();

  // The employee, department and fine are the workflow's to decide, from the session.
  return useGlobalMutation({
    mutationFn: (input: Parameters<typeof submitAttendanceCorrection>[0]) =>
      translateRuleErrors(() => submitAttendanceCorrection(input)),
    onSuccess: (result) => {
      // Also the pending-today check, which hides the widget's clock-in.
      queryClient.invalidateQueries({ queryKey: ['hr-attendance-corrections'] });
      // CORRECTION_REQUEST_SUBMITTED_NO_HOB when no head of business was found to notify.
      toast({
        title: t(result.Data?.MessageKey || 'CORRECTION_REQUEST_SUBMITTED', {
          defaultValue: result.Message || t('CORRECTION_REQUEST_SUBMITTED'),
        }),
      });
    },
  });
};
